import {Alert,Box,Button,Card,CardContent,Typography} from '@mui/material';
import React,{useEffect,useRef,useState} from 'react';
import {LessonStep} from '../../types';

interface Props {
  step: LessonStep;
  token: string | null;
  onNext: (score?: number) => void;
  onBackToLetters?: () => void;
  lessonId?: string;
  isLastStep?: boolean;
}

const WritingStep: React.FC<Props> = ({step,token,onNext,onBackToLetters,isLastStep=false}) => {
  const canvasRef=useRef<HTMLCanvasElement|null>(null);
  const pointer=useRef<number|null>(null);
  const completed=useRef(false);
  const [hasDrawing,setHasDrawing]=useState(false);
  const [error,setError]=useState('');
  const target=typeof step.content.target_text==='string'?step.content.target_text:
    typeof step.content.letter==='string'?step.content.letter:'';
  const available=target.trim().length>0 && Array.from(target).length<=100;

  useEffect(()=>{
    pointer.current=null;completed.current=false;setHasDrawing(false);setError('');
    const canvas=canvasRef.current;const context=canvas?.getContext('2d');
    if(canvas&&context)context.clearRect(0,0,canvas.width,canvas.height);
  },[step.id,target,token]);

  const point=(event:React.PointerEvent<HTMLCanvasElement>)=>{
    const canvas=event.currentTarget;const rect=canvas.getBoundingClientRect();
    if(!rect.width||!rect.height)return null;
    return {x:(event.clientX-rect.left)*canvas.width/rect.width,y:(event.clientY-rect.top)*canvas.height/rect.height};
  };
  const start=(event:React.PointerEvent<HTMLCanvasElement>)=>{
    if(pointer.current!==null || (event.pointerType!=='touch' && event.button!==0))return;
    const canvas=event.currentTarget;const location=point(event);const context=canvas.getContext('2d');
    if(!location||!context){setError('Поле письма недоступно в этом браузере.');return;}
    event.preventDefault();setError('');pointer.current=event.pointerId;
    try{canvas.setPointerCapture?.(event.pointerId);}catch{/* Drawing can continue within the canvas. */}
    context.lineWidth=5;context.lineCap='round';context.lineJoin='round';context.strokeStyle='#1a5f2a';
    context.beginPath();context.moveTo(location.x,location.y);context.lineTo(location.x,location.y);context.stroke();
    setHasDrawing(true);
  };
  const move=(event:React.PointerEvent<HTMLCanvasElement>)=>{
    if(pointer.current!==event.pointerId)return;
    const location=point(event);const context=event.currentTarget.getContext('2d');
    if(location&&context){event.preventDefault();context.lineTo(location.x,location.y);context.stroke();}
  };
  const stop=(event:React.PointerEvent<HTMLCanvasElement>)=>{if(pointer.current===event.pointerId)pointer.current=null;};
  const clear=()=>{
    pointer.current=null;const canvas=canvasRef.current;const context=canvas?.getContext('2d');
    if(canvas&&context)context.clearRect(0,0,canvas.width,canvas.height);
    setHasDrawing(false);setError('');
  };

  if(!available)return <Card><CardContent><Typography variant="h5">{step.title}</Typography>
    <Alert severity="error">В материале не указан текст для письма или он имеет неподдерживаемый формат.</Alert>
    {onBackToLetters&&<Button onClick={onBackToLetters}>К выбору буквы</Button>}
  </CardContent></Card>;
  return <Card><CardContent>
    <Typography variant="h5">{step.title}</Typography>
    {typeof step.content.instruction==='string'&&step.content.instruction&&<Typography sx={{my:1}}>{step.content.instruction}</Typography>}
    <Typography variant="h3" dir="auto" sx={{fontFamily:'Noto Naskh Arabic, serif',my:2}}>{target}</Typography>
    <Alert severity="info">Оценка письма и порядок штрихов недоступны. Это самостоятельная тренировка: рисунок не сохраняется и не отправляется.</Alert>
    {error&&<Alert severity="error">{error}</Alert>}
    <Typography sx={{mt:2}}>Рисуйте мышью, пером или пальцем.</Typography>
    <Box sx={{my:2}}><canvas ref={canvasRef} width={350} height={250}
      aria-label="Поле для самостоятельной тренировки письма"
      style={{display:'block',maxWidth:'100%',border:'2px solid #2e7d32',borderRadius:8,touchAction:'none',cursor:'crosshair'}}
      onPointerDown={start} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} onPointerLeave={stop} onLostPointerCapture={stop}/></Box>
    {isLastStep&&<Alert severity="info">Это последний шаг: завершение сохранит вашу отметку о прохождении без оценки навыка.</Alert>}
    <Box sx={{display:'flex',gap:1,mt:2}}>
      <Button onClick={clear} disabled={!hasDrawing}>Очистить</Button>
      <Button variant="contained" disabled={!hasDrawing} onClick={()=>{if(!completed.current){completed.current=true;onNext();}}}>Продолжить без оценки</Button>
    </Box>
  </CardContent></Card>;
};

export default WritingStep;
